import React, { useEffect, useMemo, useState, useCallback } from "react";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { useBrands } from "../contexts/BrandContext";
import { Button } from "./ui/button";
import { LogIn, MapPin, Clock, Calendar as CalendarIcon, Globe2 } from "lucide-react";
import { toast } from "sonner";
import { formatTimezoneLabel, timezoneFull } from "../lib/timezone";

// --- Helpers temps ---
const pad2 = (n) => String(n).padStart(2, "0");
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const nowHHMM = () => {
  const d = new Date();
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
};
const browserTimezone = () => {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC"; } catch { return "UTC"; }
};
const hhmmToMin = (s) => {
  if (!s || typeof s !== "string") return null;
  const [h, m] = s.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Seuil par défaut pour détecter un retard / une sortie anticipée
const ECART_SEUIL_MIN = 5;
// « Pointer l'entrée » s'affiche au plus tôt 60 min avant l'heure prévue
const POINTAGE_AVANCE_MIN = 60;

// Capture la position GPS + reverse-géocoding (réutilise la logique de Pointage.jsx)
async function captureGeoLocation() {
  if (!("geolocation" in navigator)) return { timezone: browserTimezone() };
  const position = await new Promise((resolve) => {
    let done = false;
    const finish = (pos) => { if (!done) { done = true; resolve(pos); } };
    navigator.geolocation.getCurrentPosition(
      (pos) => finish(pos),
      () => finish(null),
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
    setTimeout(() => finish(null), 9000);
  });
  const tz = browserTimezone();
  if (!position) return { timezone: tz };
  const { latitude, longitude, accuracy } = position.coords;
  let address = null;
  try {
    const r = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${latitude}&lon=${longitude}&zoom=18&accept-language=fr`,
      { headers: { Accept: "application/json" } }
    );
    if (r.ok) { const j = await r.json(); address = j.display_name || null; }
  } catch { /* on garde au moins les coordonnées */ }
  return { latitude, longitude, accuracy, timezone: tz, address };
}

export default function TodayShiftsPointage() {
  const { user } = useAuth();
  const { brands, selectedBrandId } = useBrands();
  const [shifts, setShifts] = useState([]);
  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [, setTick] = useState(0); // re-render chaque minute pour activer/désactiver le bouton Pointer

  const today = todayISO();
  // Admin & manager voient TOUS les shifts du jour de leur périmètre (vue de pilotage),
  // les autres rôles voient uniquement leurs propres shifts (vue d'action).
  const isOverseer = user?.role === "admin" || user?.role === "manager";

  const [sitesById, setSitesById] = useState({});
  const [categoriesById, setCategoriesById] = useState({});

  const load = useCallback(async () => {
    if (!user?.id) return;
    try {
      const baseParams = { start_date: today, end_date: today };
      const shiftParams = isOverseer
        ? { ...baseParams, ...(selectedBrandId ? { brand_id: selectedBrandId } : {}) }
        : { ...baseParams, collaborateur_id: user.id };
      const entryParams = isOverseer
        ? { ...baseParams, ...(selectedBrandId ? { brand_id: selectedBrandId } : {}) }
        : { ...baseParams, collaborateur_id: user.id };
      const reqs = [
        api.get("/shifts", { params: shiftParams }),
        api.get("/timeentries", { params: entryParams }),
        api.get("/sites").catch(() => ({ data: [] })),
        api.get("/shift-categories").catch(() => ({ data: [] })),
      ];
      // Liste des collaborateurs uniquement utile pour les rôles de pilotage (affichage du nom).
      if (isOverseer) reqs.push(api.get("/collaborateurs"));
      const res = await Promise.all(reqs);
      setShifts(res[0].data || []);
      setEntries(res[1].data || []);
      setSitesById(Object.fromEntries((res[2]?.data || []).map((s) => [s.id, s])));
      setCategoriesById(Object.fromEntries((res[3]?.data || []).map((c) => [c.id, c])));
      if (isOverseer) setUsers(res[4]?.data || []);
    } catch {
      setShifts([]);
      setEntries([]);
      setSitesById({});
      setCategoriesById({});
      setUsers([]);
    }
  }, [user, today, isOverseer, selectedBrandId]);

  useEffect(() => { load(); }, [load]);

  // Re-render chaque minute pour activer/désactiver le bouton « Pointer l'entrée » (règle 1h)
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60_000);
    return () => clearInterval(id);
  }, []);

  // Le bouton « Pointer l'entrée » apparaît uniquement le jour J et au plus tôt 60 min avant le start_time prévu.
  const canShowClockIn = (row) => {
    if (row.shift.date !== today) return false;
    if (row.state !== "à_venir") return false;
    const startMin = hhmmToMin(row.shift.start_time);
    if (startMin == null) return false;
    const now = new Date();
    const nowMin = now.getHours() * 60 + now.getMinutes();
    return nowMin >= (startMin - POINTAGE_AVANCE_MIN);
  };

  // Associe à chaque shift le pointage correspondant (priorité au shift_id, fallback même brand_id + même collaborateur)
  // PUIS filtre pour ne garder que les shifts « à venir » (start_time futur) ET non encore pointés.
  const rows = useMemo(() => {
    const usedEntryIds = new Set();
    const enriched = shifts.map((s) => {
      // 1. Pointage avec shift_id explicite
      let entry = entries.find((e) => e.shift_id === s.id);
      // 2. Sinon, pointage du jour, même collaborateur + même département, pas encore associé
      if (!entry) {
        entry = entries.find(
          (e) =>
            !usedEntryIds.has(e.id) &&
            e.collaborateur_id === s.collaborateur_id &&
            e.brand_id === s.brand_id &&
            e.date === today,
        );
      }
      if (entry) usedEntryIds.add(entry.id);
      const brand = brands.find((b) => b.id === s.brand_id);
      const collab = users.find((u) => u.id === s.collaborateur_id);
      const site = sitesById[s.site_id] || null;
      const category = categoriesById[s.shift_category_id] || null;
      const isOwn = s.collaborateur_id === user?.id;

      // État + écarts
      const plannedStartMin = hhmmToMin(s.start_time);
      const plannedEndMin = hhmmToMin(s.end_time);
      const actualInMin = entry ? hhmmToMin(entry.clock_in) : null;
      const actualOutMin = entry ? hhmmToMin(entry.clock_out) : null;

      const lateMin = actualInMin != null && plannedStartMin != null ? actualInMin - plannedStartMin : null;
      const earlyOutMin = actualOutMin != null && plannedEndMin != null ? plannedEndMin - actualOutMin : null;

      let state = "à_venir";
      if (entry?.clock_in && entry?.clock_out) state = "termine";
      else if (entry?.clock_in) state = "en_cours";

      return { shift: s, entry, brand, collab, site, category, isOwn, state, lateMin, earlyOutMin };
    });
    // Ne garde que les shifts « à venir » (non encore pointés, ni marqués
    // absents ou indisponibles) et trie par heure de début.
    const onlyFuture = enriched.filter(
      (r) =>
        r.state === "à_venir" &&
        r.shift.absence_status !== "absent" &&
        r.shift.unavailability_status !== "unavailable",
    );
    return onlyFuture.sort((a, b) => (a.shift.start_time || "").localeCompare(b.shift.start_time || ""));
  }, [shifts, entries, brands, today, users, user, sitesById, categoriesById]);

  const clockIn = async (row) => {
    setBusyId(row.shift.id);
    try {
      const geo = await captureGeoLocation();
      if (geo.latitude == null) {
        toast.info("Géo-localisation indisponible — pointage enregistré sans position");
      }
      await api.post("/timeentries", {
        collaborateur_id: user.id,
        brand_id: row.shift.brand_id,
        date: today,
        clock_in: nowHHMM(),
        clock_out: null,
        shift_id: row.shift.id,
        ...geo,
      });
      toast.success("Entrée pointée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de pointage");
    } finally {
      setBusyId(null);
    }
  };

  const clockOut = async (row) => {
    if (!row.entry?.id) return;
    setBusyId(row.shift.id);
    try {
      const geo = await captureGeoLocation();
      await api.put(`/timeentries/${row.entry.id}/clock_out`, null, {
        params: {
          clock_out: nowHHMM(),
          ...(geo.latitude != null ? { latitude_out: geo.latitude } : {}),
          ...(geo.longitude != null ? { longitude_out: geo.longitude } : {}),
          ...(geo.accuracy != null ? { accuracy_out: geo.accuracy } : {}),
          ...(geo.timezone ? { timezone_out: geo.timezone } : {}),
          ...(geo.address ? { address_out: geo.address } : {}),
        },
      });
      toast.success("Sortie pointée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de pointage");
    } finally {
      setBusyId(null);
    }
  };

  if (!user?.id) return null;

  return (
    <div className="bg-white border border-zinc-200 p-6 rounded-sm" data-testid="today-shifts-pointage">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
        <div>
          <p className="label-uppercase mb-1">Pointage à venir du jour</p>
          <h2 className="font-display font-semibold text-xl text-zinc-900">
            {isOverseer ? "Shifts à venir aujourd'hui" : "Mes shifts à venir"}
          </h2>
          <p className="text-xs text-zinc-500 font-mono mt-1 inline-flex items-center gap-1">
            <CalendarIcon size={11} /> {today}
            {isOverseer ? (
              <>
                <span className="mx-1.5 text-zinc-300">·</span>
                Vue pilotage{selectedBrandId ? " (département filtré)" : (user?.role === "manager" ? " (votre périmètre)" : " (tous départements)")}
              </>
            ) : (
              <>
                <span className="mx-1.5 text-zinc-300">·</span>
                <MapPin size={11} /> Géo-localisation activée
              </>
            )}
          </p>
        </div>
        <span className="text-[11px] font-mono px-2 py-1 bg-zinc-50 border border-zinc-200 rounded-sm text-zinc-700">
          {rows.length} à venir
        </span>
      </div>

      {rows.length === 0 ? (
        <p className="text-sm text-zinc-500 font-mono py-6 text-center" data-testid="no-shifts-today">
          Aucun pointage à venir aujourd'hui
        </p>
      ) : (
        <div className="space-y-3">
          {rows.map((row) => {
            const { shift, brand, collab, site, category, isOwn } = row;
            const isBusy = busyId === shift.id;
            const canAct = !isOverseer || isOwn;
            return (
              <div
                key={shift.id}
                className="border border-zinc-200 rounded-sm p-3 flex items-center gap-3 flex-wrap"
                data-testid={`today-shift-row-${shift.id}`}
              >
                <span className="h-10 w-1 rounded-sm flex-shrink-0" style={{ backgroundColor: brand?.color || "#0055FF" }} />
                <div className="flex-1 min-w-[180px]">
                  {isOverseer && (
                    <p className="text-sm font-semibold text-zinc-900" data-testid={`today-shift-collab-${shift.id}`}>
                      {collab?.name || "—"}
                      {isOwn && <span className="ml-1.5 text-[10px] uppercase font-semibold px-1.5 py-0.5 bg-zinc-900 text-white rounded-sm tracking-wider">Moi</span>}
                    </p>
                  )}
                  <p className={`${isOverseer ? "text-xs text-zinc-600" : "text-sm font-medium text-zinc-900"}`}>
                    {brand?.name || "—"} {shift.poste && <span className="text-zinc-500 font-normal">· {shift.poste}</span>}
                  </p>
                  <p className="text-xs text-zinc-600 font-mono mt-0.5 inline-flex items-center gap-1.5">
                    <Clock size={11} className="text-zinc-400" />
                    Planifié : {shift.start_time}–{shift.end_time}
                    {brand?.timezone && (() => {
                      const tz = formatTimezoneLabel(brand.timezone);
                      return tz.full ? (
                        <span
                          className="inline-flex items-center gap-1 text-zinc-500 ml-1"
                          title={timezoneFull(brand.timezone)}
                          data-testid={`today-shift-tz-${shift.id}`}
                        >
                          <Globe2 size={10} className="text-zinc-400" /> {tz.full}
                        </span>
                      ) : null;
                    })()}
                  </p>
                  {(site || category) && (
                    <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5 text-xs text-zinc-600 font-mono">
                      {site && (
                        <span className="inline-flex items-center gap-1.5" data-testid={`today-shift-site-${shift.id}`}>
                          <MapPin size={11} className="text-zinc-400" /> {site.name}
                        </span>
                      )}
                      {category && (
                        <span className="inline-flex items-center gap-1.5" data-testid={`today-shift-category-${shift.id}`}>
                          <span
                            className="h-2 w-2 rounded-full"
                            style={{ backgroundColor: category.color || "#71717A" }}
                          />
                          {category.name}
                        </span>
                      )}
                    </div>
                  )}
                  <div className="flex gap-1.5 mt-1.5 flex-wrap">
                    <span className="text-[10px] uppercase font-semibold px-2 py-0.5 bg-zinc-50 text-zinc-600 border border-zinc-200 rounded-sm tracking-wider">
                      À venir
                    </span>
                  </div>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  {canAct && canShowClockIn(row) && (
                    <Button
                      onClick={() => clockIn(row)}
                      disabled={isBusy}
                      size="sm"
                      className="bg-[#0055FF] hover:bg-[#0044CC] rounded-sm"
                      data-testid={`clock-in-shift-${shift.id}`}
                    >
                      <LogIn size={12} className="mr-1" /> {isBusy ? "…" : "Pointer l'entrée"}
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
